import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Stack,
  Avatar,
  Typography,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";
import { fetchInstagramProfile } from "../../../services/instagramApi";

const StatCard = ({ label, value }) => (
  <Box sx={{ textAlign: "center", flex: 1 }}>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {typeof value === "number" ? value.toLocaleString() : value ?? "—"}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

const InstagramDetailsDialog = ({ open, onClose }) => {
  const workspaceId = useSelector(
    (s) => s.workspace?.currentWorkspace?._id || s.auth?.user?.workspaceId
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);

  const load = useCallback(async () => {
    if (!workspaceId) {
      setLoading(false);
      setError("No workspace selected.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      let token = getAccessToken();
      if (!token) token = await getAuthToken();
      if (!token) {
        setError("Not authenticated.");
        return;
      }
      const data = await fetchInstagramProfile(workspaceId, token);
      setProfile(data);
    } catch (e) {
      setError(
        e.response?.data?.message || e.message || "Failed to load Instagram details."
      );
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { bgcolor: "background.paper" } }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        Instagram Integration Details
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
          <Button
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={load}
            disabled={loading}
            variant="outlined"
          >
            Refresh
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading && !profile && !error && (
          <Box sx={{ py: 3, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {profile && (
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Avatar src={profile.profilePicture} sx={{ width: 64, height: 64 }} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  @{profile.username}
                </Typography>
                {profile.name && (
                  <Typography variant="body2" color="text.secondary">
                    {profile.name}
                  </Typography>
                )}
                <Stack direction="row" gap={1} sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={profile.status === "CONNECTED" ? "Connected" : profile.status || "Unknown"}
                    color={profile.status === "CONNECTED" ? "success" : "default"}
                    variant="outlined"
                  />
                  {profile.accountType && (
                    <Chip size="small" label={profile.accountType} variant="outlined" />
                  )}
                </Stack>
              </Box>
            </Stack>

            <Divider />

            <Stack direction="row">
              <StatCard label="Followers" value={profile.followersCount} />
              <StatCard label="Following" value={profile.followsCount} />
              <StatCard label="Media" value={profile.mediaCount} />
            </Stack>

            <Typography variant="caption" color="text.secondary">
              Instagram User ID: {profile.instagramUserId}
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default InstagramDetailsDialog;
